import React, {useEffect, useMemo, useState} from "react";
import {Mutex} from "async-mutex";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import {ConfirmDeleteDialog, FloatingAddButton} from "@atttomyx/react-components";
import {EventDialog, TaskDialog} from "../dialogs";
import {taskService} from "../../services";
import {arrays, datetime, objects, users} from "@atttomyx/shared-utils";
import {calendarUtils} from "../../utils";
import {DATE_ONCE, DATE_YEARLY} from "../../constants";
import "./calendarPage.css";

const eventsMutex = new Mutex();
const datesMutex = new Mutex();

const CalendarPage = (props) => {
    const { dimensions, snackbar, dateModes, nodes, tasks, date, setDate,
        onSaveTask, onDeleteTask, onGotoNode } = props;
    const [ events, setEvents ] = useState([]);
    const [ event, setEvent ] = useState(null);
    const [ task, setTask ] = useState(null);
    const [ deleting, setDeleting ] = useState(null);

    const year = useMemo(() => {
        return parseInt(datetime.getYear(date));
    }, [date]);

    useEffect(() => {
        eventsMutex.acquire()
        .then(function(release) {
            try {
                const events = [];
                const last = year - 1;
                const next = year + 1;

                nodes.forEach(node => {
                    const dates = objects.defaultIfNullOrUndefined(node.dates, []);
                    const who = dimensions.wide ? users.getFullName(node) : users.getInitials(node);
                    let i = 0;

                    dates.filter(date => calendarUtils.isVisible(date, dateModes)).forEach(date => {
                        const mode = dateModes[date.label];

                        if (mode === DATE_YEARLY) {
                            const monthDay = date.value.substring(4);
                            const thisYear = year + monthDay;
                            const month = parseInt(date.value.substring(5, 7));

                            arrays.addTo(events, calendarUtils.nodeToEvent(node, date, who, thisYear, i++));

                            if (month === 12) {
                                const lastYear = last + monthDay;

                                arrays.addTo(events, calendarUtils.nodeToEvent(node, date, who, lastYear, i++));

                            } else if (month === 1) {
                                const nextYear = next + monthDay;

                                arrays.addTo(events, calendarUtils.nodeToEvent(node, date, who, nextYear, i++));
                            }

                        } else if (mode === DATE_ONCE) {
                            // todo: performance enhancement - filter out dates that are not this year, last year (Dec), or next year (Jan)
                            arrays.addTo(events, calendarUtils.nodeToEvent(node, date, who, date.value, i++));
                        }
                    });
                });

                tasks.forEach(task => {
                    // todo: performance enhancement - filter out tasks that are not this year, last year (Dec), or next year (Jan)
                    arrays.addTo(events, calendarUtils.taskToEvent(task));
                });

                setEvents(events);
            } finally {
                release();
            }
        }.bind(this));
    }, [dimensions.wide, dateModes, nodes, tasks, year]);

    const dateRangeChanged = (info) => {
        const start = datetime.getDate(info.view.currentStart.toISOString());

        if (start !== date) {
            datesMutex.acquire()
            .then(function(release) {
                try {
                    if (start !== date) {
                        setDate(start);
                    }
                } finally {
                    release();
                }
            }.bind(this));
        }
    };

    return <div className="calendar-page">
        <div className="calendar">
            <FullCalendar
                initialView="dayGridMonth"
                initialDate={date}
                plugins={[dayGridPlugin]}
                contentHeight={calendarUtils.getContentHeight()}
                height="100%"
                expandRows={true}
                displayEventTime={false}
                displayEventEnd={false}
                fixedWeekCount={false}
                eventDisplay="block"
                eventOrder={["start", "attribute"]}
                events={events}
                eventClick={info => setEvent(info.event)}
                datesSet={dateRangeChanged}
                headerToolbar={{
                    left: dimensions.wide ? 'title' : '',
                    right: 'today prev,next',
                }}
                footerToolbar={dimensions.wide ? null : {
                    left: 'title',
                }}
            />
        </div>
        <FloatingAddButton
            title="Add event"
            position={!dimensions.landscape || !dimensions.short ? "higher" : undefined}
            center={!dimensions.short}
            onClick={() => setTask({})}
        />
        {event ? <EventDialog
            event={event.extendedProps}
            onCancel={() => setEvent(null)}
            onGotoNode={onGotoNode}
            onEditTask={(obj) => {
                setEvent(null);
                setTask(obj);
            }}
            onDeleteTask={(obj) => {
                setEvent(null);
                setDeleting(obj);
            }}
        /> : null}
        {task ? <TaskDialog
            snackbar={snackbar}
            task={task}
            onCancel={() => setTask(null)}
            onSave={(saved) => {
                setTask(null);
                onSaveTask(saved);
            }}
        /> : null}
        {deleting ? <ConfirmDeleteDialog
            snackbar={snackbar}
            title={deleting.title}
            onCancel={() => setDeleting(null)}
            onDelete={(taskId) => {
                setDeleting(null);
                onDeleteTask(taskId);
            }}
            deleter={(success, failure) => taskService.deleteTask(deleting.id, success, failure)}
        /> : null}
    </div>
};

export default CalendarPage;
